/* Bugünkü menü, footer ve sayfa başlıkları — blok belgesi olarak.

   Tasarım belgesinde bir parça yoksa (editör o bölgeye hiç dokunmadıysa)
   üretici ve editör bunları kullanıyor. Metinler 2026-10-18'deki
   partials/header|menu|footer.html ve works|blog|404.html şablonlarından
   birebir; anahtarlar bugünkü i18n anahtarları. */

import type { Block, Design, Rich } from '@frawgraphy/shared';
import type { SiteContent } from '../content.ts';
import { defaultHome } from './home.ts';

const t = (tr: string): Rich => ({ tr });

export function defaultShared(): Block[] {
  const nav = (anchor: string, key: string, label: string) => ({ id: `nav-${anchor}`, key: `nav.${key}`, label: t(label), href: `#${anchor}` });
  return [
    {
      id: 'site-nav',
      type: 'nav',
      brandSub: t('Moda Stüdyosu'),
      location: t('Beyoğlu, İstanbul — 41°02′N 28°58′E'),
      items: [
        nav('work', 'work', 'İşler'),
        nav('video', 'video', 'Videografi'),
        nav('digital', 'digital', 'Dijital Üretim'),
        nav('studio', 'studio', 'Stüdyo'),
        nav('services', 'services', 'Hizmetler'),
        nav('contact', 'contact', 'İletişim')
      ]
    },
    {
      id: 'site-footer',
      type: 'footer',
      mark: t('FRAWGRAPHY'),
      links: [
        { id: 'footer-journal', key: 'footer.journal', label: t('Günlük'), href: 'blog.html' },
        { id: 'footer-instagram', label: t('Instagram'), href: '#work', lang: 'en' },
        { id: 'footer-behance', label: t('Behance'), href: '#work', lang: 'en' },
        { id: 'footer-press', key: 'footer.press', label: t('Basın dosyası'), href: '#contact' }
      ],
      legal: t('© 2026 Frawgraphy <span lang="en">Studio</span> — Tüm hakları saklıdır.')
    }
  ];
}

function head(prefix: string, index: string, title: string, lede: string): Block[] {
  /* Kimlik küçük harf (şema kuralı), anahtar bugünkü i18n anahtarı (`notFound.index`). */
  const id = prefix.toLowerCase();
  return [
    { id: `${id}-index`, key: `${prefix}.index`, type: 'eyebrow', text: t(index) },
    { id: `${id}-title`, key: `${prefix}.title`, type: 'heading', level: 1, text: t(title) },
    { id: `${id}-lede`, key: `${prefix}.lede`, type: 'paragraph', variant: 'lede', text: t(lede) }
  ];
}

export type PageName = 'works' | 'blog' | 'notFound';

export function defaultPage(name: PageName): Block[] {
  switch (name) {
    case 'works':
      return [
        {
          id: 'page-works',
          type: 'page',
          head: head('works', 'Arşiv', 'Bütün <em>işler</em>', 'Editoryal seriler, sezon kampanyaları ve güzellik çekimleri bir arada. Kadraja tıklayın — kare tam çözünürlükte açılır.'),
          blocks: []
        }
      ];
    case 'blog':
      return [
        {
          id: 'page-blog',
          type: 'page',
          head: [
            ...head('blog', 'Günlük', 'Nasıl <em>yapıyoruz</em>', 'Sette öğrendiklerimizi yazıyoruz: ışık şemaları, çekim öncesi hazırlık, renk kararları ve yeni araçların işimize değdiği yerler.'),
            { id: 'blog-notice', key: 'blog.notice', type: 'note', variant: 'lang', text: t('Yazılar Türkçe yayımlanmaktadır.') }
          ],
          blocks: []
        }
      ];
    case 'notFound':
      return [
        {
          id: 'page-404',
          type: 'page',
          head: head('notFound', '404', 'Bu kare <em>yok</em>', 'Aradığınız sayfa taşınmış ya da hiç var olmamış olabilir. Aşağıdaki üç yer stüdyonun bütün işini taşıyor.'),
          blocks: [
            {
              id: 'notfound-cta',
              key: 'notFound',
              type: 'pagecta',
              title: t('Nereye bakılır'),
              text: t('Seçili işler, günlük ve iletişim — hepsi bir tık uzakta.'),
              buttons: [
                { id: 'notfound-work', key: 'notFound.ctaWork', label: t('Seçili işler'), href: '/works', variant: 'primary', arrow: true },
                { id: 'notfound-blog', key: 'notFound.ctaBlog', label: t('Günlük'), href: '/blog', variant: 'plain' },
                { id: 'notfound-contact', key: 'notFound.ctaContact', label: t('İletişim'), href: '/#contact', variant: 'plain' }
              ]
            }
          ]
        }
      ];
  }
}

/** Belgenin eksik parçalarını bugünkü sitenin karşılığıyla doldurur. */
export type ResolvedDesign = { tokens: Design['tokens']; home: Block[]; shared: Block[]; pages: Record<PageName, Block[]> };

export function resolveDesign(design: Design, content: SiteContent): ResolvedDesign {
  return {
    tokens: design.tokens,
    home: design.home ?? defaultHome(content),
    shared: design.shared ?? defaultShared(),
    pages: {
      works: design.pages?.works ?? defaultPage('works'),
      blog: design.pages?.blog ?? defaultPage('blog'),
      notFound: design.pages?.notFound ?? defaultPage('notFound')
    }
  };
}
